import React, { useEffect, useState } from 'react';
import { Plus, Package, AlertTriangle, Trash2, Edit3, ArrowLeft, Search } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import MobileLayout from '../components/layout/MobileLayout';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const emptyForm = { name: '', sku: '', stock: 0, low_stock_threshold: 5 };

const Inventory = () => {
    const { authAxios, user } = useAuth();
    const navigate = useNavigate();
    const role = user?.team_role || 'owner';
    const canEdit = role === 'owner' || role === 'manager';

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [lowOnly, setLowOnly] = useState(false);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const res = await authAxios.get('/inventory', { params: { low_stock_only: lowOnly } });
            setItems(res.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load inventory');
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [lowOnly]);

    const filtered = items.filter((i) => {
        if (!q) return true;
        const s = q.toLowerCase();
        return (i.name || '').toLowerCase().includes(s) || (i.sku || '').toLowerCase().includes(s);
    });

    const lowCount = items.filter((i) => i.stock <= i.low_stock_threshold).length;

    const openCreate = () => {
        setEditingId(null);
        setForm(emptyForm);
        setDialogOpen(true);
    };

    const openEdit = (item) => {
        setEditingId(item.id);
        setForm({
            name: item.name || '',
            sku: item.sku || '',
            stock: item.stock || 0,
            low_stock_threshold: item.low_stock_threshold || 0
        });
        setDialogOpen(true);
    };

    const submit = async () => {
        if (!form.name.trim()) { toast.error('Name is required'); return; }
        setSaving(true);
        try {
            if (editingId) {
                await authAxios.patch(`/inventory/${editingId}`, form);
                toast.success('Item updated');
            } else {
                await authAxios.post('/inventory', form);
                toast.success('Item added');
            }
            setDialogOpen(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save');
        } finally { setSaving(false); }
    };

    const remove = async (id) => {
        if (!window.confirm('Delete this item?')) return;
        try {
            await authAxios.delete(`/inventory/${id}`);
            toast.success('Deleted');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        }
    };

    const adjust = async (item, delta) => {
        const newStock = Math.max(0, (item.stock || 0) + delta);
        try {
            await authAxios.patch(`/inventory/${item.id}`, { stock: newStock });
            setItems((prev) => prev.map((i) => i.id === item.id ? { ...i, stock: newStock } : i));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to adjust stock');
        }
    };

    return (
        <MobileLayout>
            <div className="space-y-4" data-testid="inventory-page">
                <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/settings')} data-testid="inventory-back-btn">
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <h1 className="text-2xl font-bold font-heading flex-1">Inventory</h1>
                    {canEdit && (
                        <Button onClick={openCreate} size="sm" data-testid="add-item-btn">
                            <Plus className="w-4 h-4 mr-1" /> Add
                        </Button>
                    )}
                </div>

                {/* Summary */}
                <div className="grid grid-cols-2 gap-3">
                    <Card><CardContent className="p-3">
                        <div className="flex items-center gap-2 text-muted-foreground text-xs"><Package className="w-3 h-3" /> Total items</div>
                        <p className="text-2xl font-bold font-number mt-1" data-testid="inventory-total-count">{items.length}</p>
                    </CardContent></Card>
                    <Card className={lowCount > 0 ? 'border-destructive/50' : ''}>
                        <CardContent className="p-3">
                            <div className="flex items-center gap-2 text-muted-foreground text-xs"><AlertTriangle className="w-3 h-3" /> Low stock</div>
                            <p className={`text-2xl font-bold font-number mt-1 ${lowCount > 0 ? 'text-destructive' : ''}`} data-testid="inventory-low-count">{lowCount}</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Filters */}
                <div className="flex gap-2">
                    <div className="relative flex-1">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or SKU…" className="pl-9" data-testid="inventory-search-input" />
                    </div>
                    <Button variant={lowOnly ? 'default' : 'outline'} onClick={() => setLowOnly((x) => !x)} data-testid="inventory-low-filter-btn">
                        <AlertTriangle className="w-4 h-4" />
                    </Button>
                </div>

                {/* List */}
                {loading ? (
                    <Card><CardContent className="p-6 text-center text-muted-foreground">Loading…</CardContent></Card>
                ) : filtered.length === 0 ? (
                    <Card><CardContent className="p-8 text-center text-muted-foreground">
                        <Package className="w-10 h-10 mx-auto mb-2 opacity-40" />
                        {items.length === 0 ? 'No items yet. Add your first product.' : 'No items match your search.'}
                    </CardContent></Card>
                ) : (
                    <div className="space-y-2">
                        {filtered.map((item) => {
                            const isLow = item.stock <= item.low_stock_threshold;
                            return (
                                <Card key={item.id} className={isLow ? 'border-destructive/40' : ''} data-testid={`inventory-item-${item.id}`}>
                                    <CardContent className="p-3">
                                        <div className="flex items-center gap-3">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <p className="font-semibold truncate">{item.name}</p>
                                                    {isLow && <Badge variant="destructive" className="text-xs">Low</Badge>}
                                                </div>
                                                {item.sku && <p className="text-xs text-muted-foreground font-number">SKU: {item.sku}</p>}
                                                <p className="text-xs text-muted-foreground mt-0.5">Threshold: {item.low_stock_threshold}</p>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => adjust(item, -1)} disabled={item.stock === 0} data-testid={`stock-minus-${item.id}`}>−</Button>
                                                <div className="w-10 text-center font-number font-bold text-lg" data-testid={`stock-value-${item.id}`}>{item.stock}</div>
                                                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => adjust(item, 1)} data-testid={`stock-plus-${item.id}`}>+</Button>
                                            </div>
                                            {canEdit && (
                                                <div className="flex flex-col gap-1">
                                                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(item)} data-testid={`edit-item-${item.id}`}>
                                                        <Edit3 className="w-3.5 h-3.5" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove(item.id)} data-testid={`delete-item-${item.id}`}>
                                                        <Trash2 className="w-3.5 h-3.5 text-destructive" />
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}

                {/* Add/Edit dialog */}
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent data-testid="inventory-dialog">
                        <DialogHeader><DialogTitle>{editingId ? 'Edit Item' : 'Add Item'}</DialogTitle></DialogHeader>
                        <div className="space-y-3 py-2">
                            <div>
                                <label className="text-sm font-medium">Name *</label>
                                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Sugar 1kg" className="mt-1" data-testid="item-name-input" />
                            </div>
                            <div>
                                <label className="text-sm font-medium">SKU / Code</label>
                                <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} placeholder="Optional" className="mt-1 font-number" data-testid="item-sku-input" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-sm font-medium">Current Stock</label>
                                    <Input type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: parseInt(e.target.value || '0', 10) })} className="mt-1 font-number" data-testid="item-stock-input" />
                                </div>
                                <div>
                                    <label className="text-sm font-medium">Low-stock at</label>
                                    <Input type="number" min="0" value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: parseInt(e.target.value || '0', 10) })} className="mt-1 font-number" data-testid="item-threshold-input" />
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                            <Button onClick={submit} disabled={saving} data-testid="save-item-btn">{saving ? 'Saving…' : 'Save'}</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </MobileLayout>
    );
};

export default Inventory;
